import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { billingApi, BillingStatus, AvailablePlan } from "../../services/api";
import { useToast } from "../Toast";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import WarningIcon from "@mui/icons-material/Warning";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { parseServerDate } from "../../utils/formatters";
import "./BillingManagement.css";

type BillingCycle = "monthly" | "yearly";

const BillingManagement: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [availablePlans, setAvailablePlans] = useState<AvailablePlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  
  // Initialize showSuccessModal directly from URL to avoid flicker on first render
  const [showSuccessModal, setShowSuccessModal] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("billing") === "success";
  });

  // Ref to track if we've already handled the billing param (prevents double-processing in StrictMode)
  const billingParamHandled = useRef(false);

  const loadBillingData = useCallback(async (isPostPayment: boolean = false, retryCount: number = 0) => {
    setIsLoading(true);
    // Don't show error during post-payment as webhook might still be processing
    if (!isPostPayment) {
      setError("");
    }

    try {
      const [status, plansResponse] = await Promise.all([
        billingApi.getStatus(),
        billingApi.getAvailablePlans()
      ]);
      setBillingStatus(status);
      setAvailablePlans(plansResponse.plans);
      setError("");

      // If post-payment and subscription not active/trialing yet, retry (max 5 times with 1.5s delay)
      const isActiveOrTrialing = status.subscription_status === "active" || status.subscription_status === "trialing";
      if (isPostPayment && !isActiveOrTrialing && retryCount < 5) {
        setTimeout(() => loadBillingData(true, retryCount + 1), 1500);
        return;
      }

      if (isPostPayment && isActiveOrTrialing && retryCount > 0) {
        showToast("success", status.subscription_status === "trialing"
          ? "Free trial started successfully!"
          : "Subscription activated successfully!");
      }
    } catch (err) {
      // On post-payment, retry as webhook might not have processed yet
      if (isPostPayment && retryCount < 5) {
        setTimeout(() => loadBillingData(true, retryCount + 1), 1500);
        return;
      }

      if (err instanceof Error && err.message.includes("not configured")) {
        setError("Billing is not configured. Please contact support.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to load billing information");
      }
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  // Helper to clean URL without triggering React Router re-renders
  const cleanUrlParams = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("billing");
    window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : ""));
  }, []);

  // Check for billing success/canceled query params
  useEffect(() => {
    const billingResult = searchParams.get("billing");

    // Only process billing param once
    if (billingResult && !billingParamHandled.current) {
      billingParamHandled.current = true;
      
      if (billingResult === "success") {
        // Modal is already shown (initialized from URL in useState)
        // Remove billing param from URL without triggering re-renders
        cleanUrlParams();
        // Load data with post-payment retry logic
        loadBillingData(true);
      } else if (billingResult === "canceled") {
        showToast("error", "Subscription checkout was canceled.");
        cleanUrlParams();
        loadBillingData(false);
      }
    } else if (!billingResult && !billingParamHandled.current) {
      // Normal load (no billing param)
      loadBillingData(false);
      billingParamHandled.current = true;
    }
  }, [searchParams, showToast, loadBillingData, cleanUrlParams]);

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const handleSubscribe = async (planId: string) => {
    setIsRedirecting(true);
    setSelectedPlanId(planId);
    try {
      const response = await billingApi.createCheckoutSession(planId, billingCycle);
      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to start checkout"
      );
      setIsRedirecting(false);
      setSelectedPlanId(null);
    }
  };

  const handleManageBilling = async () => {
    setIsRedirecting(true);
    try {
      const response = await billingApi.createPortalSession();
      window.location.href = response.portal_url;
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to open billing portal"
      );
      setIsRedirecting(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "active":
        return <span className="billing-status-badge active">Active</span>;
      case "trialing":
        return <span className="billing-status-badge active">Free Trial</span>;
      case "past_due":
        return <span className="billing-status-badge past-due">Past Due</span>;
      case "canceled":
        return <span className="billing-status-badge canceled">Canceled</span>;
      default:
        return <span className="billing-status-badge no-subscription">No Subscription</span>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return parseServerDate(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);

  const formatCentsPrice = (cents: number) => formatPrice(cents / 100);

  /**
   * Get the display price for a plan based on the selected billing cycle.
   * Prefers Stripe prices (fetched via lookup keys) if available,
   * falls back to database prices otherwise.
   */
  const getPlanPrice = (plan: AvailablePlan): string => {
    if (billingCycle === "monthly") {
      // Prefer Stripe price
      if (plan.stripe_monthly_price) {
        return formatCentsPrice(plan.stripe_monthly_price.amount);
      }
      return formatPrice(plan.price);
    } else {
      // Yearly
      if (plan.stripe_yearly_price) {
        return formatCentsPrice(plan.stripe_yearly_price.amount);
      }
      return formatPrice(plan.yearly_price);
    }
  };

  /**
   * Check if a plan has pricing available for the selected billing cycle.
   */
  const hasPriceForCycle = (plan: AvailablePlan): boolean => {
    if (billingCycle === "monthly") {
      return !!(plan.stripe_monthly_price || plan.price > 0 || plan.stripe_price_id);
    }
    return !!(plan.stripe_yearly_price || plan.yearly_price > 0);
  };

  /**
   * Calculate monthly equivalent for yearly price (for savings display).
   */
  const getYearlySavings = (plan: AvailablePlan): string | null => {
    const monthlyAmount = plan.stripe_monthly_price?.amount || (plan.price * 100);
    const yearlyAmount = plan.stripe_yearly_price?.amount || (plan.yearly_price * 100);
    
    if (!monthlyAmount || !yearlyAmount || yearlyAmount <= 0) return null;
    
    const annualMonthly = monthlyAmount * 12;
    const savings = annualMonthly - yearlyAmount;
    
    if (savings <= 0) return null;
    
    const savingsPercent = Math.round((savings / annualMonthly) * 100);
    return `Save ${savingsPercent}%`;
  };

  // When success modal is showing, render ONLY the modal with a stable background
  // This prevents any content changes that cause visual flicker
  if (showSuccessModal) {
    return (
      <div className="billing-management">
        <div className="admin-section-header">
          <h2>Billing & Subscription</h2>
        </div>
        <div className="billing-success-backdrop" />
        <div className="billing-success-modal-overlay" onClick={handleCloseSuccessModal}>
          <div className="billing-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="billing-success-icon">
              <CheckCircleIcon style={{ fontSize: 64, color: "#10b981" }} />
            </div>
            <h2>Payment Successful!</h2>
            <p>Your subscription has been activated successfully.</p>
            <p style={{ fontSize: "0.875rem", color: "#64748b", marginTop: 8 }}>
              Thank you for your subscription. You now have full access to all features included in your plan.
            </p>
            <button
              className="billing-btn billing-btn-primary"
              onClick={handleCloseSuccessModal}
              style={{ marginTop: 24, width: "100%" }}
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state (only when NOT in post-payment success flow)
  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading billing information...</p>
      </div>
    );
  }

  // Show error
  if (error) {
    return (
      <div className="admin-error">
        {error}
        <button onClick={() => loadBillingData(false)} style={{ marginLeft: 16 }}>
          Retry
        </button>
      </div>
    );
  }

  // System tenant (exempt from billing)
  if (billingStatus?.is_system_tenant) {
    return (
      <div className="billing-management">
        <div className="admin-section-header">
          <h2>Billing & Subscription</h2>
        </div>

        <div className="billing-card billing-card-system">
          <div className="billing-card-header">
            <div className="billing-card-icon">
              <VerifiedUserIcon style={{ fontSize: 32 }} />
            </div>
            <div>
              <h3>System Tenant</h3>
              <p className="billing-card-subtitle">
                Your organization is exempt from billing requirements.
              </p>
            </div>
          </div>
          <div className="billing-status-row">
            <span className="billing-label">Status</span>
            {getStatusBadge("active")}
          </div>
        </div>
      </div>
    );
  }

  const hasActiveSubscription =
    billingStatus?.subscription_status === "active" ||
    billingStatus?.subscription_status === "trialing";
  const isTrialing = billingStatus?.subscription_status === "trialing";
  const isPastDue = billingStatus?.subscription_status === "past_due";
  const isCanceled = billingStatus?.subscription_status === "canceled";
  const hasNoSubscription = !billingStatus?.subscription_status;
  const currentPlanName = billingStatus?.current_plan_name ?? null;
  const currentPlan = availablePlans.find(p => p.name === currentPlanName) ?? null;
  const canManageBilling = !!(hasActiveSubscription || isPastDue) && !!billingStatus?.stripe_customer_id;
  // When canceled, the tenant's old plan is no longer "current" — it's a previous plan they can resubscribe to.
  const isEnterprisePlanName = currentPlanName?.toLowerCase() === "enterprise";
  const isEnterpriseCurrent = isEnterprisePlanName && !isCanceled;
  const isEnterprisePrevious = isEnterprisePlanName && isCanceled;
  const enterpriseBillingDate = isEnterpriseCurrent
    ? isTrialing && billingStatus?.trial_end
      ? `Trial ends: ${formatDate(billingStatus.trial_end)}`
      : billingStatus?.current_period_end
      ? `Next billing: ${formatDate(billingStatus.current_period_end)}`
      : null
    : null;

  const getPlanCTA = (plan: AvailablePlan): { label: string; action: "subscribe" | "manage" | "current" | "resubscribe" | "none" } => {
    if (plan.name === currentPlanName) {
      // Canceled: the previous plan should be re-subscribable, not a dead-end disabled button.
      if (isCanceled) {
        return hasPriceForCycle(plan)
          ? { label: "Resubscribe", action: "resubscribe" }
          : { label: "Not Available", action: "none" };
      }
      return { label: "Current Plan", action: "current" };
    }

    if (hasActiveSubscription || isPastDue) {
      const planMonthlyPrice = plan.stripe_monthly_price?.amount ?? (plan.price * 100);
      const currentMonthlyPrice = currentPlan?.stripe_monthly_price?.amount ?? ((currentPlan?.price ?? 0) * 100);
      const label = planMonthlyPrice > currentMonthlyPrice ? "Upgrade →" : "Downgrade →";
      return { label, action: "manage" };
    }

    if (!hasPriceForCycle(plan)) return { label: "Not Available", action: "none" };
    return { label: "Subscribe", action: "subscribe" };
  };

  return (
    <div className="billing-management">
      {/* Header with status badge + Manage Subscription button */}
      <div className="admin-section-header">
        <div className="billing-header-row">
          <h2>Billing & Subscription</h2>
          {billingStatus && (
            <div className="billing-header-status">
              {getStatusBadge(billingStatus.subscription_status)}
              {canManageBilling && (
                <button
                  className="billing-btn billing-btn-secondary billing-btn-sm"
                  onClick={handleManageBilling}
                  disabled={isRedirecting}
                >
                  {isRedirecting && !selectedPlanId ? (
                    <><span className="billing-btn-spinner" /> Redirecting...</>
                  ) : (
                    <><SettingsIcon style={{ fontSize: 16 }} /> Manage Subscription</>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Past Due Warning Banner */}
      {isPastDue && (
        <div className="billing-warning-banner">
          <WarningIcon style={{ fontSize: 20 }} />
          <div>
            <strong>Payment Past Due</strong>
            <p>
              Your payment could not be processed. Your account is in read-only
              mode. Please update your payment method to restore full access.
            </p>
          </div>
        </div>
      )}

      {/* Canceled Notice Banner */}
      {isCanceled && (
        <div className="billing-warning-banner billing-warning-banner--info">
          <WarningIcon style={{ fontSize: 20 }} />
          <div>
            <strong>Subscription Canceled</strong>
            <p>
              Your subscription has been canceled. Choose a plan below to
              reactivate your account and restore full access.
            </p>
          </div>
        </div>
      )}

      {/* Plans Section — always visible */}
      <div className="billing-plans-header">
        <h3>
          {hasActiveSubscription || isPastDue
            ? "Available Plans"
            : hasNoSubscription || isCanceled
            ? "Choose Your Plan"
            : "Subscription Plans"}
        </h3>

        {/* Billing Cycle Toggle */}
        {/* <div className="billing-cycle-toggle">
          <button
            className={`billing-cycle-btn ${billingCycle === "monthly" ? "active" : ""}`}
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly
          </button>
          <button
            className={`billing-cycle-btn ${billingCycle === "yearly" ? "active" : ""}`}
            onClick={() => setBillingCycle("yearly")}
          >
            Yearly
            <span className="billing-cycle-badge">Save up to 20%</span>
          </button>
        </div> */}
      </div>

      <div className="billing-plans-grid">
        {availablePlans
          .filter((p) => p.name.toLowerCase() !== "enterprise")
          .map((plan) => {
            const isCurrentPlan = plan.name === currentPlanName && !isCanceled;
            const isPreviousPlan = plan.name === currentPlanName && isCanceled;
            const yearlySavings = billingCycle === "yearly" ? getYearlySavings(plan) : null;
            const cta = getPlanCTA(plan);

            const billingDateLabel = isCurrentPlan
              ? isTrialing && billingStatus?.trial_end
                ? `Trial ends: ${formatDate(billingStatus.trial_end)}`
                : billingStatus?.current_period_end
                ? `Next billing: ${formatDate(billingStatus.current_period_end)}`
                : null
              : null;

            return (
              <div
                key={plan.id}
                className={`billing-plan-card${isCurrentPlan ? " billing-plan-card--current" : ""}`}
              >
                {yearlySavings && !isCurrentPlan && (
                  <div className="plan-savings-badge">{yearlySavings}</div>
                )}

                <div className="plan-header">
                  {isCurrentPlan && (
                    <div className="plan-current-badge">Current Plan</div>
                  )}
                  {isPreviousPlan && (
                    <div className="plan-previous-badge">Previous Plan</div>
                  )}
                  <h4 className="plan-name">{plan.name}</h4>
                  <div className="plan-price">
                    <span className="plan-price-amount">{getPlanPrice(plan)}</span>
                    <span className="plan-price-period">
                      /{billingCycle === "monthly" ? "month" : "year"}
                    </span>
                  </div>
                  {billingCycle === "yearly" && plan.price > 0 && (
                    <p className="plan-monthly-equiv">
                      {formatPrice((plan.stripe_yearly_price?.amount || plan.yearly_price * 100) / 100 / 12)}/month billed annually
                    </p>
                  )}
                  {billingDateLabel && (
                    <p className="plan-billing-date">{billingDateLabel}</p>
                  )}
                </div>

                <ul className="plan-features">
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    {plan.max_domains} Domain{plan.max_domains !== 1 ? "s" : ""}
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    {plan.max_clusters} Keyword Clusters
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    {plan.max_keywords_per_cluster} Keywords per Cluster
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    {plan.max_users} Team Member{plan.max_users !== 1 ? "s" : ""}
                  </li>
                </ul>

                {cta.action === "current" ? (
                  <button className="billing-btn billing-btn--current" disabled>
                    Current Plan
                  </button>
                ) : cta.action === "manage" ? (
                  <button
                    className="billing-btn"
                    onClick={handleManageBilling}
                    disabled={isRedirecting}
                  >
                    {isRedirecting && !selectedPlanId ? (
                      <><span className="billing-btn-spinner" /> Redirecting...</>
                    ) : (
                      cta.label
                    )}
                  </button>
                ) : cta.action === "subscribe" || cta.action === "resubscribe" ? (
                  <button
                    className="billing-btn billing-btn-primary plan-subscribe-btn"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isRedirecting}
                  >
                    {isRedirecting && selectedPlanId === plan.id ? (
                      <><span className="billing-btn-spinner" /> Redirecting...</>
                    ) : (
                      cta.label
                    )}
                  </button>
                ) : (
                  <button className="billing-btn billing-btn--current" disabled>
                    Not Available
                  </button>
                )}
              </div>
            );
          })}

        {/* Enterprise plan — always shown as the last card */}
        <div className="billing-plan-card">
          <div className="plan-header">
            {isEnterpriseCurrent ? (
              <div className="plan-current-badge plan-current-badge--enterprise">Current Plan</div>
            ) : isEnterprisePrevious ? (
              <div className="plan-previous-badge">Previous Plan</div>
            ) : (
              <div className="plan-enterprise-badge">Enterprise</div>
            )}
            <h4 className="plan-name">Enterprise</h4>
            <div className="plan-price">
              <span className="plan-price-amount">Custom</span>
            </div>
            {enterpriseBillingDate ? (
              <p className="plan-billing-date">{enterpriseBillingDate}</p>
            ) : (
              <p className="plan-enterprise-tagline">Tailored for growing teams</p>
            )}
          </div>

          <ul className="plan-features">
            <li><CheckIcon style={{ fontSize: 16 }} />Customized Number of Domains</li>
            <li><CheckIcon style={{ fontSize: 16 }} />Customized Number of Keyword Clusters</li>
            <li><CheckIcon style={{ fontSize: 16 }} />Customized Number of Keywords per Cluster</li>
            <li><CheckIcon style={{ fontSize: 16 }} />Customized Number of Team Members</li>
            <li><CheckIcon style={{ fontSize: 16 }} />Dedicated Account Manager</li>
            <li><CheckIcon style={{ fontSize: 16 }} />Priority Support & SLA</li>
          </ul>

          {isEnterpriseCurrent ? (
            <button className="billing-btn billing-btn--current" disabled>
              Current Plan
            </button>
          ) : (
            <a
              className="billing-btn billing-btn--enterprise"
              href='https://clusterview.ai/contact'
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Sales
            </a>
          )}
        </div>
      </div>

      {availablePlans.filter((p) => p.name.toLowerCase() !== "enterprise").length === 0 && (
        <p className="billing-no-plans-hint">
          No subscription plans configured yet. A superadmin needs to set up Stripe prices in Subscription Management.
        </p>
      )}

      {/* Info Section */}
      <div className="billing-info">
        <h4>Billing Information</h4>
        <ul>
          <li>
            <strong>Secure Payments:</strong> All payments are processed securely through Stripe.
          </li>
          <li>
            <strong>Manage Billing:</strong> Use the Manage Subscription button to update payment methods, view invoices, or cancel your subscription.
          </li>
          {isPastDue && (
            <li className="billing-info-warning">
              <strong>Past Due Notice:</strong> Your account is in read-only mode. Update your payment method to restore full access.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default BillingManagement;
